import { useEffect, useState, useCallback } from "react";
import {
  fetchExpenditures,
  fetchExpenditureSummary,
  castVote,
} from "../api/api";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Skeleton,
  SkeletonText,
  Stack,
  useBreakpointValue,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  UnorderedList,
  ListItem,
  Link,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Button,
  Heading,
} from "@chakra-ui/react";
import { motion, useAnimation } from "framer-motion";
import { FaInfoCircle, FaLock, FaRedo } from "react-icons/fa";
import VoteToReveal from "./VoteToReveal";

// ─── Config ───────────────────────────────────────────────────────
const SAFFRON = "#E07B1A";
const SAFFRON_LIGHT = "#FAC775";
const SAFFRON_PALE = "#FAEEDA";
const SAFFRON_DARK = "#854F0B";
const DEEP = "#412402";
const CONSTRUCTION_ESTIMATE = 10_000_000; // ₹1 Cr — only used once revealed

const MotionBox = motion(Box);

const formatINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

// ── Hoisted outside the component ──────────────────────────────────
// These previously lived inside Expenditures() as inline function
// definitions. That meant React saw a *new* component type on every
// re-render (loading → loaded, tab focus, etc.) and fully remounted
// them each time — replaying their entrance animation from scratch.
// That's what caused the fast "blink". Stable top-level components
// fix it: React now just updates props instead of remounting.

const ContactModal = ({ isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} isCentered>
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>Contact Admin</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <VStack align="start" spacing={3}>
          <Text fontWeight="bold">Phone</Text>
          <HStack>
            <Link href="tel:+919493575676" fontWeight="semibold">
              📞 +91-9493575676
            </Link>
            <Button size="sm" variant="ghost" as="a" href="tel:+919493575676">
              Call
            </Button>
          </HStack>
          <Text fontWeight="bold" mt={2}>
            Email
          </Text>
          <HStack>
            <Link href="mailto:sasvsathokada@gmail.com" fontWeight="semibold">
              sasvsathokada@gmail.com
            </Link>
            <Button
              size="sm"
              variant="ghost"
              as="a"
              href="mailto:sasvsathokada@gmail.com"
            >
              Email
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={3}>
            Clicking "Call" will open the device dialer on mobile.
          </Text>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

const Notes = ({ onOpen }) => (
  <MotionBox
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    mb={5}
    p={4}
    borderRadius="lg"
    bgGradient="linear(to-r, orange.50, orange.100)"
    boxShadow="sm"
  >
    <Alert status="info" variant="left-accent" alignItems="flex-start">
      <AlertIcon />
      <Box>
        <Heading as="h4" size="sm" mb={2} color="orange.700">
          Notes on donations &amp; spendings
        </Heading>
        <UnorderedList spacing={2} ml={4}>
          <ListItem>
            Donations may be <b>paid in instalments</b>. Example: pledged{" "}
            {formatINR(100000)} — paid {formatINR(20000)} now, remaining{" "}
            {formatINR(80000)} later.
          </ListItem>
          <ListItem>
            Spendings are recorded when <b>work is completed or billed</b>.
          </ListItem>
          <ListItem>
            Some updates can be <b>delayed</b> (bank processing, instalment
            promises, or manual verification).
          </ListItem>
          <ListItem>
            Multi-stage jobs show the amount when that stage is finished — final
            totals update after completion.
          </ListItem>
          <ListItem>
            Questions or discrepancies?{" "}
            <Link
              onClick={onOpen}
              color="orange.600"
              _hover={{ textDecoration: "underline" }}
            >
              Contact the admin
            </Link>{" "}
            — tap to view phone &amp; email.
          </ListItem>
        </UnorderedList>
      </Box>
    </Alert>
  </MotionBox>
);

const ProgressSection = ({
  summaryLoading,
  revealed,
  totalCollected,
  totalSpent,
  progressPct,
}) => {
  if (summaryLoading) {
    return (
      <Box mb={6}>
        <Skeleton height="16px" borderRadius="full" mb={4} />
        <Stack direction={{ base: "column", md: "row" }} spacing={4}>
          <Skeleton height="90px" borderRadius="xl" flex={1} />
          <Skeleton height="90px" borderRadius="xl" flex={1} />
        </Stack>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      mb={6}
    >
      {revealed && (
        <Box mb={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Text fontSize="sm" fontWeight={600} color="orange.700">
              Construction progress
            </Text>
            <Text fontSize="sm" fontWeight={700} color="orange.600">
              {progressPct}% complete
            </Text>
          </Box>
          <Box
            w="100%"
            h="16px"
            bg="orange.100"
            borderRadius="full"
            overflow="hidden"
            border="1px solid"
            borderColor="orange.200"
          >
            <Box
              h="100%"
              w={`${progressPct}%`}
              borderRadius="full"
              style={{
                background: `linear-gradient(90deg, ${SAFFRON}, ${SAFFRON_LIGHT})`,
                transition: "width 1s ease",
              }}
            />
          </Box>
          <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
            Estimated target: ₹1,00,00,000
          </Text>
        </Box>
      )}

      <Stack direction={{ base: "column", md: "row" }} spacing={4}>
        <Box
          flex={1}
          position="relative"
          p={4}
          borderRadius="xl"
          bg={SAFFRON_PALE}
          border="1px solid"
          borderColor="orange.200"
          overflow="hidden"
        >
          <Text fontWeight={700} color="green.700" fontSize="sm" mb={1}>
            Total Collected
          </Text>
          {revealed ? (
            <Text fontSize="xl" fontWeight={700} color="green.600">
              {formatINR(totalCollected)}
            </Text>
          ) : (
            <>
              <Text
                fontSize="xl"
                fontWeight={700}
                color="green.600"
                opacity={0}
              >
                ₹0
              </Text>
              <Box
                position="absolute"
                inset={0}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                <Box as={FaLock} color={SAFFRON_DARK} boxSize={5} />
                <Text
                  fontSize="11px"
                  color="gray.500"
                  textAlign="center"
                  lineHeight={1.4}
                >
                  Revealed by committee
                </Text>
              </Box>
            </>
          )}
        </Box>

        <Box
          flex={1}
          position="relative"
          p={4}
          borderRadius="xl"
          bg={SAFFRON_PALE}
          border="1px solid"
          borderColor="orange.200"
          overflow="hidden"
        >
          <Text fontWeight={700} color="red.700" fontSize="sm" mb={1}>
            Total Spent
          </Text>
          {revealed ? (
            <Text fontSize="xl" fontWeight={700} color="red.600">
              {formatINR(totalSpent)}
            </Text>
          ) : (
            <>
              <Text fontSize="xl" fontWeight={700} color="red.600" opacity={0}>
                ₹0
              </Text>
              <Box
                position="absolute"
                inset={0}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                <Box as={FaLock} color={SAFFRON_DARK} boxSize={5} />
                <Text
                  fontSize="11px"
                  color="gray.500"
                  textAlign="center"
                  lineHeight={1.4}
                >
                  Revealed by committee
                </Text>
              </Box>
            </>
          )}
        </Box>
      </Stack>

      {!revealed && (
        <Box
          mt={3}
          p={3}
          borderRadius="lg"
          bg="orange.50"
          border="1px solid"
          borderColor="orange.200"
          display="flex"
          alignItems="flex-start"
          gap={2}
        >
          <Text fontSize="lg" lineHeight={1} mt="1px">
            🔒
          </Text>
          <Text fontSize="xs" color={DEEP} lineHeight={1.6}>
            Exact totals will be publicly disclosed once the committee decides.
            The spending list below remains fully transparent.{" "}
            <b>జై శ్రీ విశ్వేశ్వర స్వామి 🙏</b>
          </Text>
        </Box>
      )}
    </MotionBox>
  );
};

const ExpenditureListSkeleton = () => (
  <Stack spacing={3} mt={4}>
    {[1, 2, 3, 4].map((i) => (
      <Box key={i} p={4} borderRadius="xl" border="1px solid #eee">
        <SkeletonText noOfLines={2} spacing={2} skeletonHeight="3" />
      </Box>
    ))}
  </Stack>
);

const ExpenditureError = ({ onRetry }) => (
  <Box
    textAlign="center"
    py={10}
    px={4}
    borderRadius="xl"
    bg="red.50"
    border="1px solid"
    borderColor="red.200"
    mt={4}
  >
    <Text fontWeight={600} color="red.600" mb={3}>
      Couldn't load spending records — check your connection.
    </Text>
    <Button
      leftIcon={<FaRedo />}
      size="sm"
      colorScheme="red"
      variant="outline"
      onClick={onRetry}
    >
      Try again
    </Button>
  </Box>
);

// ── Main component ───────────────────────────────────────────────────
export default function Expenditures() {
  const [expenditures, setExpenditures] = useState([]);
  const [summary, setSummary] = useState({
    revealed: false,
    totalCollected: null,
    totalSpent: null,
  });

  const [expLoading, setExpLoading] = useState(true);
  const [expError, setExpError] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const scrollControls = useAnimation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const sortByDateDesc = (list) => {
    if (!Array.isArray(list)) return [];
    return list.slice().sort((a, b) => {
      const da = a?.date ? new Date(a.date) : new Date(0);
      const db = b?.date ? new Date(b.date) : new Date(0);
      return db - da;
    });
  };

  const loadExpenditures = useCallback(async () => {
    setExpLoading(true);
    setExpError(false);
    try {
      const res = await fetchExpenditures();
      setExpenditures(sortByDateDesc(res.data || []));
    } catch (err) {
      console.error(
        "❌ /api/expenditures failed —",
        err?.response?.status,
        err?.message,
      );
      setExpError(true);
    } finally {
      setExpLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await fetchExpenditureSummary();
      setSummary(
        res.data || { revealed: false, totalCollected: null, totalSpent: null },
      );
    } catch (err) {
      console.error(
        "❌ /api/expenditures/summary failed —",
        err?.response?.status,
        err?.message,
      );
      setSummary({ revealed: false, totalCollected: null, totalSpent: null });
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExpenditures();
    loadSummary();
  }, [loadExpenditures, loadSummary]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadExpenditures();
        loadSummary();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [loadExpenditures, loadSummary]);

  useEffect(() => {
    if (isMobile && expenditures.length > 0) {
      const loopScroll = async () => {
        while (true) {
          const totalHeight =
            expenditures.length * 150 + window.innerHeight * 0.6;
          await scrollControls.start({
            y: ["0px", `-${totalHeight}px`],
            transition: { duration: expenditures.length * 3, ease: "linear" },
          });
          await scrollControls.start({ y: 0, transition: { duration: 0 } });
        }
      };
      loopScroll();
      return () => scrollControls.stop();
    }
    return () => {
      scrollControls.stop();
    };
  }, [isMobile, expenditures, scrollControls]);

  const handleVote = async () => {
    await castVote();
  };

  const cardGradients = [
    "linear(to-r, orange.50, orange.100)",
    "linear(to-r, yellow.50, yellow.100)",
    "linear(to-r, teal.50, green.50)",
    "linear(to-r, pink.50, rose.50)",
  ];

  const { revealed, totalCollected, totalSpent } = summary;

  const progressPct = revealed
    ? Math.min(Math.round((totalSpent / CONSTRUCTION_ESTIMATE) * 100), 100)
    : null;

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1000px" mx="auto" overflow="hidden">
      <Heading
        mb={3}
        textAlign="center"
        color="orange.500"
        fontSize={{ base: "2xl", md: "3xl" }}
      >
        Construction Spendings
      </Heading>

      <Notes onOpen={onOpen} />

      {isMobile && (
        <IconButton
          aria-label="Contact admin"
          icon={<FaInfoCircle />}
          position="fixed"
          bottom="20px"
          right="16px"
          zIndex={999}
          boxShadow="lg"
          borderRadius="full"
          onClick={onOpen}
        />
      )}

      <ContactModal isOpen={isOpen} onClose={onClose} />

      {/* ── Construction progress + totals (server-gated, own skeleton) ── */}
      <ProgressSection
        summaryLoading={summaryLoading}
        revealed={revealed}
        totalCollected={totalCollected}
        totalSpent={totalSpent}
        progressPct={progressPct}
      />

      {/* ── Vote to request early reveal — hidden once revealed or loading ── */}
      {!summaryLoading && !revealed && <VoteToReveal onVote={handleVote} />}

      {/* ── Expenditure list — own loading / error state ── */}
      {expLoading ? (
        <ExpenditureListSkeleton />
      ) : expError ? (
        <ExpenditureError onRetry={loadExpenditures} />
      ) : isMobile ? (
        <Box position="relative" height="550px" overflow="hidden" mt={4}>
          <motion.div animate={scrollControls}>
            <Stack spacing={4}>
              {expenditures.map((e, idx) => (
                <Card
                  key={e.id}
                  borderRadius="xl"
                  boxShadow="md"
                  p={3}
                  bgGradient={cardGradients[idx % cardGradients.length]}
                  transform="scale(0.98)"
                  _hover={{ transform: "scale(1)", boxShadow: "lg" }}
                  transition="all 0.3s ease"
                >
                  <CardHeader pb={2}>
                    <Text fontWeight="bold" color="gray.800">
                      {e.description}
                    </Text>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Stack spacing={1}>
                      <Text fontSize="sm" color="gray.600">
                        Paid To: <b>{e.paidTo}</b>
                      </Text>
                      {(e.mode || e.category) && (
                        <Badge colorScheme="blue" width="fit-content">
                          {e.mode || e.category}
                        </Badge>
                      )}
                      <Divider />
                      <Text fontSize="md" color="red.600" fontWeight="semibold">
                        Amount: {formatINR(e.amount)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(e.date).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Text>
                    </Stack>
                  </CardBody>
                </Card>
              ))}
            </Stack>
          </motion.div>
        </Box>
      ) : (
        <TableContainer
          border="1px solid #ddd"
          borderRadius="lg"
          boxShadow="sm"
          bg="white"
        >
          <Table variant="simple" size="sm">
            <Thead bg="orange.500" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th color="white">Description</Th>
                <Th color="white">Paid To</Th>
                <Th color="white">Mode</Th>
                <Th color="white" isNumeric>
                  Amount (₹)
                </Th>
                <Th color="white">Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {expenditures.map((e, idx) => (
                <Tr
                  key={e.id}
                  _hover={{ bg: "orange.50" }}
                  bg={idx % 2 === 0 ? "orange.50" : "orange.100"}
                  transition="background 0.3s ease"
                >
                  <Td>{e.description}</Td>
                  <Td>{e.paidTo}</Td>
                  <Td>{e.mode || e.category || "—"}</Td>
                  <Td isNumeric>{formatINR(e.amount)}</Td>
                  <Td>
                    {new Date(e.date).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
